// H6 : connecteurs et intégrations (charte D-038, « exceptions d'abord »). Contrats d'interface, états (non
// raccordé, en test, actif) avec transitions gardées par le serveur, configuration non secrète et secret
// jamais relu, essai, journal des échanges, livraisons des webhooks avec relance, clés d'API par organisation
// (affichées une seule fois). Aucun connecteur n'est simulé actif ; l'administration est réservée à
// l'administrateur national, la lecture à la direction générale.

import { useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { api } from '../../api/client';
import { aUnRole, useSession } from '../../app/session';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { date, dateHeure } from '../../composants/format';
import '../inspections/terrain.css';

interface Libelle {
  code: string;
  libelle: string;
}
interface ReferentielIntegrations {
  types: Libelle[];
  etats: Libelle[];
  evenements: Libelle[];
  permissionsCles: Libelle[];
}
interface Contrat {
  nature: string;
  frequence: string;
  format: string;
  authentification: string;
  responsabilites: string;
  procedureIndisponibilite: string;
  donneesTransmises: string;
}
interface Connecteur {
  id: string;
  code: string;
  nom: string;
  type: string;
  description: string;
  contrat: Contrat;
  etat: 'non_raccorde' | 'en_test' | 'actif';
  motifEtat: string | null;
  etatModifieLe: string | null;
  configuration: Record<string, unknown>;
  secretDefini: boolean;
  evenements: string[];
  dernierEchangeLe: string | null;
  echecs24h?: number;
  configurationIncomplete?: string | null;
}
interface EntreeJournal {
  id: string;
  integrationNom?: string | null;
  sens: 'entrant' | 'sortant';
  operation: string;
  resultat: 'succes' | 'echec' | 'refuse' | 'ignore';
  codeHttp: number | null;
  dureeMs: number | null;
  nombreElements: number | null;
  message: string;
  horodatage: string;
}
interface Livraison {
  id: string;
  evenement: string;
  etat: string;
  tentatives: number;
  prochaineTentativeLe: string;
  derniereReponseCode: number | null;
  derniereErreur: string | null;
  livreeLe: string | null;
  creeLe: string;
}
interface CleApi {
  id: string;
  organisationNom: string;
  libelle: string;
  prefixe: string;
  permissions: string[];
  expireLe: string | null;
  derniereUtilisationLe: string | null;
  revoqueeLe: string | null;
  motifRevocation: string | null;
  creeLe: string;
  auteurPrenom: string | null;
  auteurNom: string | null;
}

const LIBELLES_RESULTATS: Record<EntreeJournal['resultat'], string> = { succes: 'Succès', echec: 'Échec', refuse: 'Refusé', ignore: 'Ignoré' };
const LIBELLES_LIVRAISONS: Record<string, string> = { en_attente: 'En attente', livree: 'Livrée', echec_definitif: 'Échec définitif', abandonnee: 'Abandonnée' };

function useReferentiel() {
  return useQuery({ queryKey: ['integrations', 'referentiel'], queryFn: async () => (await api<ReferentielIntegrations>('/api/v1/integrations/referentiel')).donnees, staleTime: 5 * 60_000 });
}
const libelle = (l: Libelle[] | undefined, c: string) => l?.find((x) => x.code === c)?.libelle ?? c;

function classeEtatConnecteur(etat: Connecteur['etat']): string {
  return etat === 'en_test' ? 'etiquette ambre' : 'etiquette';
}

function useAdministrateur() {
  const { etat } = useSession();
  return aUnRole(etat, 'administrateur_national');
}

export function PageIntegrations() {
  const [params, setParams] = useSearchParams();
  const ref = useReferentiel();
  const administrateur = useAdministrateur();
  const onglet = params.get('onglet') ?? 'connecteurs';
  const connecteurs = useQuery({ queryKey: ['integrations', 'liste'], queryFn: async () => (await api<Connecteur[]>('/api/v1/integrations')).donnees });
  const cles = useQuery({ queryKey: ['integrations', 'cles'], queryFn: async () => (await api<CleApi[]>('/api/v1/cles-api')).donnees });
  const choisir = (o: string) => {
    const p = new URLSearchParams(params);
    p.set('onglet', o);
    setParams(p, { replace: true });
  };
  const liste = connecteurs.data ?? [];
  const enEchec = liste.filter((c) => (c.echecs24h ?? 0) > 0);
  const enTest = liste.filter((c) => c.etat === 'en_test');
  const dans30Jours = Date.now() + 30 * 86_400_000;
  const expirant = (cles.data ?? []).filter((k) => !k.revoqueeLe && k.expireLe && new Date(k.expireLe).getTime() < dans30Jours);
  const exceptions = [
    ...enEchec.map((c) => ({ cle: `echec-${c.id}`, ton: 'rouge', marque: 'Échanges, échecs sur 24 heures', titre: `${c.nom} : ${formaterNombre(c.echecs24h ?? 0)} échec${(c.echecs24h ?? 0) > 1 ? 's' : ''}`, detail: 'Consulter le journal des échanges du connecteur', lien: `/integrations/${c.id}` })),
    ...enTest.map((c) => ({ cle: `test-${c.id}`, ton: 'ambre', marque: 'Connecteur, en test', titre: c.nom, detail: 'Activation possible après un échange réussi constaté pendant le test', lien: `/integrations/${c.id}` })),
    ...expirant.map((k) => ({ cle: `cle-${k.id}`, ton: 'ambre', marque: 'Clé d’API, expiration proche', titre: `${k.libelle}, ${k.organisationNom}`, detail: `Expire le ${date(k.expireLe)}`, lien: '/integrations?onglet=cles' })),
  ];
  const actifs = liste.filter((c) => c.etat === 'actif').length;

  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Intégrations</h1>
          <p>
            {connecteurs.data ? `${formaterNombre(liste.length)} connecteurs décrits par leur contrat d’interface, ${formaterNombre(actifs)} actif${actifs > 1 ? 's' : ''}. ` : ''}
            Aucun connecteur n’est simulé actif : tout connecteur naît non raccordé et ne devient actif qu’après un échange réussi.
          </p>
        </div>
      </div>

      {exceptions.length > 0 && (
        <section className="a-traiter" aria-labelledby="titre-a-traiter-integrations">
          <h2 id="titre-a-traiter-integrations" className="sur-titre">
            Ce qui demande une action
          </h2>
          <ol className="liste-exceptions">
            {exceptions.map((e, i) => (
              <li key={e.cle}>
                <Link className={`exception ${e.ton}`} to={e.lien}>
                  <span className="numero" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="texte">
                    <span className="marque-exception">{e.marque}</span>
                    <b>{e.titre}</b>
                    <small>{e.detail}</small>
                  </span>
                  <Icone nom="chevron" />
                </Link>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="onglets" role="tablist" aria-label="Intégrations">
        {[
          { cle: 'connecteurs', libelle: 'Connecteurs' },
          { cle: 'cles', libelle: 'Clés d’API' },
          { cle: 'journal', libelle: 'Journal des échanges' },
        ].map((o) => (
          <button key={o.cle} type="button" role="tab" id={`onglet-${o.cle}`} aria-selected={onglet === o.cle} aria-controls={`panneau-${o.cle}`} onClick={() => choisir(o.cle)}>
            {o.libelle}
          </button>
        ))}
      </div>

      {onglet === 'connecteurs' && (
        <div id="panneau-connecteurs" role="tabpanel" aria-labelledby="onglet-connecteurs">
          {connecteurs.isLoading && <Squelette lignes={6} />}
          {connecteurs.error && <MessageErreur erreur={connecteurs.error} />}
          {connecteurs.data && (
            <div className="tableau-conteneur">
              <table className="tableau lignes-48">
                <caption className="visuellement-cache">Connecteurs</caption>
                <thead>
                  <tr>
                    <th scope="col">Connecteur</th>
                    <th scope="col">État</th>
                    <th scope="col" className="nombre masquable">
                      Échecs sur 24 h
                    </th>
                    <th scope="col" className="masquable">
                      Dernier échange
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {liste.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <Link className="lien-ligne" to={`/integrations/${c.id}`}>
                          {c.nom}
                        </Link>
                        <small>{libelle(ref.data?.types, c.type)}</small>
                      </td>
                      <td>
                        <span className={classeEtatConnecteur(c.etat)}>{libelle(ref.data?.etats, c.etat)}</span>
                      </td>
                      <td className="nombre masquable">{formaterNombre(c.echecs24h ?? 0)}</td>
                      <td className="masquable">{c.dernierEchangeLe ? dateHeure(c.dernierEchangeLe) : 'Aucun échange'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {onglet === 'cles' && (
        <div id="panneau-cles" role="tabpanel" aria-labelledby="onglet-cles">
          <ClesApi cles={cles} referentiel={ref.data} administrateur={administrateur} />
        </div>
      )}
      {onglet === 'journal' && (
        <div id="panneau-journal" role="tabpanel" aria-labelledby="onglet-journal">
          <JournalGlobal />
        </div>
      )}
    </div>
  );
}

function TableauJournal({ lignes, avecConnecteur }: { lignes: EntreeJournal[]; avecConnecteur: boolean }) {
  return (
    <div className="tableau-conteneur">
      <table className="tableau">
        <caption className="visuellement-cache">Journal des échanges</caption>
        <thead>
          <tr>
            <th scope="col">Horodatage</th>
            {avecConnecteur && <th scope="col">Connecteur</th>}
            <th scope="col">Opération</th>
            <th scope="col">Résultat</th>
            <th scope="col" className="masquable">
              Message
            </th>
          </tr>
        </thead>
        <tbody>
          {lignes.length === 0 && (
            <tr>
              <td colSpan={avecConnecteur ? 5 : 4} className="attenue">
                Aucun échange journalisé.
              </td>
            </tr>
          )}
          {lignes.map((j) => (
            <tr key={j.id}>
              <td className="horodatage">{dateHeure(j.horodatage)}</td>
              {avecConnecteur && <td>{j.integrationNom ?? 'Clé d’API'}</td>}
              <td>
                {j.operation}
                <small>
                  {j.sens === 'entrant' ? 'Entrant' : 'Sortant'}
                  {j.codeHttp ? `, HTTP ${j.codeHttp}` : ''}
                  {j.dureeMs !== null ? `, ${j.dureeMs} ms` : ''}
                </small>
              </td>
              <td>
                <span className={j.resultat === 'echec' ? 'etiquette rouge' : j.resultat === 'refuse' ? 'etiquette ambre' : 'etiquette'}>{LIBELLES_RESULTATS[j.resultat]}</span>
              </td>
              <td className="masquable">{j.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JournalGlobal() {
  const [resultat, setResultat] = useState('');
  const [sens, setSens] = useState('');
  const filtres = new URLSearchParams();
  if (resultat) filtres.set('resultat', resultat);
  if (sens) filtres.set('sens', sens);
  const journal = useQuery({ queryKey: ['integrations', 'journal', filtres.toString()], queryFn: async () => (await api<EntreeJournal[]>(`/api/v1/integrations-journal?${filtres.toString()}`)).donnees });
  return (
    <>
      <div className="barre-filtres" role="search" aria-label="Filtres du journal">
        <div className="segments" role="radiogroup" aria-label="Résultat">
          {[{ code: '', libelle: 'Tous' }, ...Object.entries(LIBELLES_RESULTATS).map(([code, l]) => ({ code, libelle: l }))].map((r) => (
            <button key={r.code || 'tous'} type="button" role="radio" aria-checked={resultat === r.code} onClick={() => setResultat(r.code)}>
              {r.libelle}
            </button>
          ))}
        </div>
        <div className="champ">
          <label htmlFor="journal-sens">Sens</label>
          <select id="journal-sens" value={sens} onChange={(e) => setSens(e.target.value)}>
            <option value="">Entrant et sortant</option>
            <option value="entrant">Entrant</option>
            <option value="sortant">Sortant</option>
          </select>
        </div>
      </div>
      {journal.isLoading && <Squelette lignes={6} />}
      {journal.error && <MessageErreur erreur={journal.error} />}
      {journal.data && <TableauJournal lignes={journal.data} avecConnecteur />}
    </>
  );
}

function ClesApi({ cles, referentiel, administrateur }: { cles: UseQueryResult<CleApi[]>; referentiel: ReferentielIntegrations | undefined; administrateur: boolean }) {
  const client = useQueryClient();
  const [creation, setCreation] = useState(false);
  const [cleUnique, setCleUnique] = useState<{ cle: string; libelle: string } | null>(null);
  const [revocation, setRevocation] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const revoquer = useMutation({
    mutationFn: async (id: string) => api(`/api/v1/cles-api/${id}/revocation`, { corps: { motif } }),
    onSuccess: async () => {
      setRevocation(null);
      setMotif('');
      await client.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
  return (
    <>
      <div className="page-tete">
        <div>
          <p>Une clé est attribuée à une organisation avec des permissions limitées ; seule son empreinte est conservée. La clé complète n’est affichée qu’une fois, à sa création.</p>
        </div>
        {administrateur && !creation && (
          <button type="button" className="bouton primaire" onClick={() => setCreation(true)}>
            <Icone nom="ajout" />
            Attribuer une clé
          </button>
        )}
      </div>
      {cleUnique && (
        <div className="cle-unique" role="status">
          <b>Clé « {cleUnique.libelle} » créée : copiez-la maintenant, elle ne sera plus jamais affichée.</b>
          <code>{cleUnique.cle}</code>
          <span>Transmettez-la à l’organisation par un canal sûr, hors de la plateforme.</span>
          <button type="button" className="bouton" onClick={() => setCleUnique(null)}>
            J’ai conservé la clé
          </button>
        </div>
      )}
      {creation && referentiel && (
        <NouvelleCle
          referentiel={referentiel}
          fermer={() => setCreation(false)}
          creee={(cle, lib) => {
            setCleUnique({ cle, libelle: lib });
            setCreation(false);
          }}
        />
      )}
      {revoquer.error && <MessageErreur erreur={revoquer.error} titre="Révocation refusée" />}
      {cles.isLoading && <Squelette lignes={4} />}
      {cles.error && <MessageErreur erreur={cles.error} />}
      {cles.data && (
        <div className="tableau-conteneur">
          <table className="tableau lignes-48">
            <caption className="visuellement-cache">Clés d’API</caption>
            <thead>
              <tr>
                <th scope="col">Clé</th>
                <th scope="col">Organisation</th>
                <th scope="col" className="masquable">
                  Permissions
                </th>
                <th scope="col" className="masquable">
                  Dernière utilisation
                </th>
                <th scope="col">État</th>
                {administrateur && (
                  <th scope="col">
                    <span className="visuellement-cache">Action</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {cles.data.length === 0 && (
                <tr>
                  <td colSpan={administrateur ? 6 : 5} className="attenue">
                    Aucune clé d’API attribuée.
                  </td>
                </tr>
              )}
              {cles.data.map((k) => (
                <tr key={k.id}>
                  <td>
                    <b>{k.libelle}</b>
                    <small>
                      ctk_{k.prefixe}_…, créée le {date(k.creeLe)}
                      {k.auteurNom ? ` par ${k.auteurPrenom} ${k.auteurNom}` : ''}
                    </small>
                  </td>
                  <td>{k.organisationNom}</td>
                  <td className="masquable">{k.permissions.map((p) => libelle(referentiel?.permissionsCles, p)).join(', ')}</td>
                  <td className="masquable">{k.derniereUtilisationLe ? dateHeure(k.derniereUtilisationLe) : 'Jamais utilisée'}</td>
                  <td>
                    {k.revoqueeLe ? (
                      <span className="etiquette gris">Révoquée</span>
                    ) : k.expireLe && new Date(k.expireLe).getTime() <= Date.now() ? (
                      <span className="etiquette gris">Expirée</span>
                    ) : (
                      <span className="etiquette">Valide{k.expireLe ? ` jusqu’au ${date(k.expireLe)}` : ''}</span>
                    )}
                    {k.motifRevocation && <small>{k.motifRevocation}</small>}
                  </td>
                  {administrateur && (
                    <td className="nombre">
                      {!k.revoqueeLe &&
                        (revocation === k.id ? (
                          <form
                            className="action-motivee"
                            onSubmit={(e: FormEvent) => {
                              e.preventDefault();
                              revoquer.mutate(k.id);
                            }}
                          >
                            <label htmlFor={`motif-cle-${k.id}`} className="visuellement-cache">
                              Motif de la révocation
                            </label>
                            <input id={`motif-cle-${k.id}`} placeholder="Motif de la révocation" value={motif} onChange={(e) => setMotif(e.target.value)} />
                            <div className="actions">
                              <button type="button" className="bouton discret" onClick={() => setRevocation(null)}>
                                Garder
                              </button>
                              <button type="submit" className="bouton danger" disabled={motif.trim().length < 3 || revoquer.isPending}>
                                Révoquer
                              </button>
                            </div>
                          </form>
                        ) : (
                          <button type="button" className="bouton" onClick={() => setRevocation(k.id)}>
                            Révoquer
                          </button>
                        ))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function NouvelleCle({ referentiel, fermer, creee }: { referentiel: ReferentielIntegrations; fermer: () => void; creee: (cle: string, libelle: string) => void }) {
  const client = useQueryClient();
  const organisations = useQuery({ queryKey: ['integrations', 'organisations'], queryFn: async () => (await api<{ id: string; nom: string; type: string }[]>('/api/v1/cles-api/organisations')).donnees });
  const [organisationId, setOrganisation] = useState('');
  const [libelleCle, setLibelle] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [expireLe, setExpire] = useState('');
  const [motif, setMotif] = useState('');
  const envoi = useMutation({
    mutationFn: async () => (await api<{ cle: string }>('/api/v1/cles-api', { corps: { organisationId, libelle: libelleCle, permissions, ...(expireLe ? { expireLe: `${expireLe}T23:59:59+01:00` } : {}), motif } })).donnees,
    onSuccess: async (d) => {
      await client.invalidateQueries({ queryKey: ['integrations'] });
      creee(d.cle, libelleCle);
    },
  });
  const pret = organisationId && libelleCle.trim().length >= 3 && permissions.length > 0 && motif.trim().length >= 3;
  return (
    <form
      className="carte-bloc carte-formulaire"
      aria-labelledby="titre-nouvelle-cle"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (pret) envoi.mutate();
      }}
    >
      <h2 id="titre-nouvelle-cle">Attribuer une clé d’API</h2>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="cle-organisation">Organisation</label>
          <select id="cle-organisation" value={organisationId} onChange={(e) => setOrganisation(e.target.value)}>
            <option value="">Choisir</option>
            {organisations.data?.map((o) => (
              <option key={o.id} value={o.id}>
                {o.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="cle-libelle">Libellé</label>
          <input id="cle-libelle" value={libelleCle} onChange={(e) => setLibelle(e.target.value)} placeholder="Par exemple : balises de la flotte" />
        </div>
        <div className="champ">
          <label htmlFor="cle-expiration">Expiration (facultative)</label>
          <input id="cle-expiration" type="date" value={expireLe} onChange={(e) => setExpire(e.target.value)} />
        </div>
      </div>
      <fieldset className="champ">
        <legend>Permissions</legend>
        {referentiel.permissionsCles.map((p) => (
          <label key={p.code} className="case">
            <input type="checkbox" checked={permissions.includes(p.code)} onChange={(e) => setPermissions((l) => (e.target.checked ? [...l, p.code] : l.filter((x) => x !== p.code)))} />
            <span>{p.libelle}</span>
          </label>
        ))}
      </fieldset>
      <div className="champ">
        <label htmlFor="cle-motif">Motif</label>
        <textarea id="cle-motif" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} />
      </div>
      {envoi.error && <MessageErreur erreur={envoi.error} titre="Attribution refusée" />}
      <div className="actions">
        <button type="button" className="bouton discret" onClick={fermer}>
          Annuler
        </button>
        <button type="submit" className="bouton primaire" disabled={!pret || envoi.isPending}>
          Créer la clé
        </button>
      </div>
    </form>
  );
}

// Fiche d'un connecteur --------------------------------------------------------------------------------

const LIBELLES_CONTRAT: [keyof Contrat, string][] = [
  ['nature', 'Nature'],
  ['frequence', 'Fréquence'],
  ['format', 'Format'],
  ['authentification', 'Authentification'],
  ['responsabilites', 'Responsabilités'],
  ['procedureIndisponibilite', 'Procédure d’indisponibilité'],
  ['donneesTransmises', 'Données transmises'],
];

const ADAPTATEURS = ['api_entrante', 'webhook_sortant', 'gps_generique', 'courriel', 'sms'];

export function FicheConnecteur() {
  const { id } = useParams();
  const client = useQueryClient();
  const ref = useReferentiel();
  const administrateur = useAdministrateur();
  const fiche = useQuery({ queryKey: ['integrations', 'connecteur', id], queryFn: async () => (await api<Connecteur>(`/api/v1/integrations/${id}`)).donnees });
  const journal = useQuery({ queryKey: ['integrations', 'connecteur', id, 'journal'], queryFn: async () => (await api<EntreeJournal[]>(`/api/v1/integrations/${id}/journal`)).donnees });
  const livraisons = useQuery({
    queryKey: ['integrations', 'connecteur', id, 'livraisons'],
    queryFn: async () => (await api<Livraison[]>(`/api/v1/integrations/${id}/livraisons`)).donnees,
    enabled: fiche.data?.type === 'webhook_sortant',
  });
  const [message, setMessage] = useState<string | null>(null);
  const rafraichir = async (texte: string) => {
    setMessage(texte);
    await client.invalidateQueries({ queryKey: ['integrations'] });
  };
  const etat = useMutation({
    mutationFn: async (p: { etat: string; motif: string }) => api(`/api/v1/integrations/${id}/etat`, { corps: p }),
    onSuccess: () => rafraichir('État du connecteur modifié.'),
  });
  const essai = useMutation({
    mutationFn: async (destinataire?: string) => (await api<unknown>(`/api/v1/integrations/${id}/essai`, { corps: destinataire ? { destinataire } : {} })).donnees,
    onSuccess: () => rafraichir('Essai effectué : son résultat figure dans le journal des échanges.'),
  });
  const relance = useMutation({
    mutationFn: async (livraisonId: string) => api(`/api/v1/integrations/livraisons/${livraisonId}/relance`, { corps: {} }),
    onSuccess: () => rafraichir('Livraison relancée : elle repart au prochain passage des tâches.'),
  });

  if (fiche.isLoading) return <Squelette lignes={8} />;
  if (fiche.error || !fiche.data) return <MessageErreur erreur={fiche.error} titre="Connecteur introuvable" />;
  const c = fiche.data;
  const adaptateur = ADAPTATEURS.includes(c.type);
  const echecsLivraison = (livraisons.data ?? []).filter((l) => l.etat === 'echec_definitif' || l.etat === 'abandonnee');

  return (
    <div>
      <div className="page-tete">
        <div>
          <span className="contexte">{libelle(ref.data?.types, c.type)}</span>
          <h1>{c.nom}</h1>
          <div className="actions">
            <span className={classeEtatConnecteur(c.etat)}>{libelle(ref.data?.etats, c.etat)}</span>
            <span className="attenue">{c.dernierEchangeLe ? `Dernier échange le ${dateHeure(c.dernierEchangeLe)}` : 'Aucun échange'}</span>
          </div>
        </div>
      </div>
      <p>{c.description}</p>
      {message && <Message type="succes">{message}</Message>}
      {[etat.error, essai.error, relance.error].filter(Boolean).map((e, i) => (
        <MessageErreur key={i} erreur={e} titre="Action refusée" />
      ))}

      <AFaireConnecteur c={c} administrateur={administrateur} adaptateur={adaptateur} echecsLivraison={echecsLivraison.length} etat={etat.mutate} essai={essai.mutate} enCours={etat.isPending || essai.isPending} />

      <div className="deux-colonnes">
        <section className="principal" aria-label="Contrat et échanges">
          <div className="carte-bloc">
            <h2>Contrat d’interface</h2>
            <dl className="contrat">
              {LIBELLES_CONTRAT.map(([cle, l]) => (
                <div key={cle}>
                  <dt>{l}</dt>
                  <dd>{c.contrat[cle]}</dd>
                </div>
              ))}
            </dl>
          </div>
          {c.type === 'webhook_sortant' && (
            <div className="carte-bloc">
              <h2>Livraisons</h2>
              {livraisons.isLoading && <Squelette lignes={3} />}
              {livraisons.data && livraisons.data.length === 0 && <p className="attenue">Aucune livraison : le connecteur n’a encore émis aucun événement.</p>}
              {livraisons.data && livraisons.data.length > 0 && (
                <div className="tableau-conteneur">
                  <table className="tableau">
                    <caption className="visuellement-cache">Livraisons du webhook</caption>
                    <thead>
                      <tr>
                        <th scope="col">Événement</th>
                        <th scope="col">État</th>
                        <th scope="col" className="masquable">
                          Tentatives
                        </th>
                        {administrateur && (
                          <th scope="col">
                            <span className="visuellement-cache">Action</span>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {livraisons.data.map((l) => (
                        <tr key={l.id}>
                          <td>
                            {libelle(ref.data?.evenements, l.evenement)}
                            <small>créée le {dateHeure(l.creeLe)}</small>
                          </td>
                          <td>
                            <span className={l.etat === 'echec_definitif' ? 'etiquette rouge' : l.etat === 'en_attente' ? 'etiquette ambre' : 'etiquette'}>{LIBELLES_LIVRAISONS[l.etat] ?? l.etat}</span>
                            {l.derniereErreur && <small>{l.derniereErreur}</small>}
                          </td>
                          <td className="masquable">
                            {l.tentatives}
                            {l.etat === 'en_attente' && <small>prochaine le {dateHeure(l.prochaineTentativeLe)}</small>}
                          </td>
                          {administrateur && (
                            <td className="nombre">
                              {(l.etat === 'echec_definitif' || l.etat === 'abandonnee') && (
                                <button type="button" className="bouton" disabled={relance.isPending} onClick={() => relance.mutate(l.id)}>
                                  Relancer
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <div className="carte-bloc">
            <h2>Journal des échanges</h2>
            {journal.isLoading && <Squelette lignes={3} />}
            {journal.data && <TableauJournal lignes={journal.data} avecConnecteur={false} />}
          </div>
        </section>
        <aside className="lateral">
          {administrateur && adaptateur && <Configuration c={c} referentiel={ref.data} surEnregistrement={rafraichir} />}
          {!adaptateur && (
            <section className="carte-bloc" aria-labelledby="titre-sans-adaptateur">
              <h2 id="titre-sans-adaptateur">Raccordement</h2>
              <p className="attenue">Aucun adaptateur n’est livré pour ce type dans cette version : le connecteur reste décrit par son contrat et non raccordé, sans simulation.</p>
            </section>
          )}
          {c.motifEtat && (
            <section className="carte-bloc" aria-labelledby="titre-dernier-etat">
              <h2 id="titre-dernier-etat">Dernier changement d’état</h2>
              <p>{c.motifEtat}</p>
              {c.etatModifieLe && <p className="attenue">le {dateHeure(c.etatModifieLe)}</p>}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function AFaireConnecteur(p: {
  c: Connecteur;
  administrateur: boolean;
  adaptateur: boolean;
  echecsLivraison: number;
  etat: (x: { etat: string; motif: string }) => void;
  essai: (destinataire?: string) => void;
  enCours: boolean;
}) {
  const { c } = p;
  const [motif, setMotif] = useState('');
  const [destinataire, setDestinataire] = useState('');
  if (!p.administrateur) return null;
  const avecDestinataire = c.type === 'courriel' || c.type === 'sms';
  const transition = (vers: string, libelleBouton: string, principale = false) => (
    <button type="button" className={principale ? 'bouton primaire' : 'bouton'} disabled={motif.trim().length < 3 || p.enCours} onClick={() => p.etat({ etat: vers, motif })}>
      {libelleBouton}
    </button>
  );
  const champMotif = (
    <div className="champ">
      <label htmlFor="motif-etat">Motif du changement d’état</label>
      <input id="motif-etat" value={motif} onChange={(e) => setMotif(e.target.value)} />
    </div>
  );
  if (c.etat === 'non_raccorde') {
    if (!p.adaptateur) return null;
    return (
      <section className={c.configurationIncomplete ? 'a-faire neutre' : 'a-faire'} aria-labelledby="titre-a-faire-connecteur">
        <div>
          <span className="sur-titre">Raccordement</span>
          <h2 id="titre-a-faire-connecteur">{c.configurationIncomplete ? 'Configuration à compléter avant tout essai' : 'Configuration complète : passer en test'}</h2>
          <p>{c.configurationIncomplete ?? 'Le passage en test ouvre les essais ; l’activation exige ensuite un échange réussi constaté.'}</p>
        </div>
        {!c.configurationIncomplete && (
          <div className="action-motivee">
            {champMotif}
            <div className="actions">{transition('en_test', 'Passer en test', true)}</div>
          </div>
        )}
      </section>
    );
  }
  return (
    <section className={c.etat === 'en_test' || p.echecsLivraison > 0 ? 'a-faire' : 'a-faire neutre'} aria-labelledby="titre-a-faire-connecteur">
      <div>
        <span className="sur-titre">{c.etat === 'en_test' ? 'En test' : 'Actif'}</span>
        <h2 id="titre-a-faire-connecteur">
          {c.etat === 'en_test' ? 'Constater un échange réussi, puis activer' : p.echecsLivraison > 0 ? `${p.echecsLivraison} livraison${p.echecsLivraison > 1 ? 's' : ''} en échec à relancer` : 'Connecteur actif'}
        </h2>
        <p>{c.etat === 'en_test' ? 'L’activation est refusée par le serveur tant qu’aucun échange réussi n’est journalisé depuis le passage en test.' : 'Toute modification technique ramène le connecteur en test.'}</p>
      </div>
      <div className="action-motivee">
        {['webhook_sortant', 'courriel', 'sms'].includes(c.type) && (
          <div className="actions">
            {avecDestinataire && (
              <div className="champ">
                <label htmlFor="essai-destinataire">{c.type === 'courriel' ? 'Adresse de l’essai' : 'Numéro de l’essai'}</label>
                <input id="essai-destinataire" value={destinataire} onChange={(e) => setDestinataire(e.target.value)} />
              </div>
            )}
            <button type="button" className="bouton" disabled={p.enCours || (avecDestinataire && destinataire.trim().length < 3)} onClick={() => p.essai(avecDestinataire ? destinataire.trim() : undefined)}>
              Lancer un essai
            </button>
          </div>
        )}
        {champMotif}
        <div className="actions">
          {c.etat === 'en_test' && transition('actif', 'Activer', true)}
          {c.etat === 'actif' && transition('en_test', 'Repasser en test')}
          {transition('non_raccorde', 'Déraccorder')}
        </div>
      </div>
    </section>
  );
}

function Configuration({ c, referentiel, surEnregistrement }: { c: Connecteur; referentiel: ReferentielIntegrations | undefined; surEnregistrement: (t: string) => Promise<void> }) {
  const conf = c.configuration as Record<string, string | number | boolean | undefined>;
  const [valeurs, setValeurs] = useState<Record<string, string>>(() => Object.fromEntries(Object.entries(conf).map(([k, v]) => [k, v === undefined ? '' : String(v)])));
  const [secret, setSecret] = useState('');
  const [evenements, setEvenements] = useState<string[]>(c.evenements);
  const [motif, setMotif] = useState('');
  const champs: { cle: string; libelle: string; type?: string }[] =
    c.type === 'webhook_sortant'
      ? [{ cle: 'url', libelle: 'Adresse HTTPS du destinataire', type: 'url' }]
      : c.type === 'courriel'
        ? [
            { cle: 'hote', libelle: 'Serveur SMTP' },
            { cle: 'port', libelle: 'Port', type: 'number' },
            { cle: 'utilisateur', libelle: 'Identifiant (facultatif)' },
            { cle: 'expediteur', libelle: 'Adresse d’expédition', type: 'email' },
          ]
        : c.type === 'sms'
          ? [
              { cle: 'url', libelle: 'Adresse de l’API du fournisseur', type: 'url' },
              { cle: 'champDestinataire', libelle: 'Champ du destinataire' },
              { cle: 'champMessage', libelle: 'Champ du message' },
              { cle: 'expediteur', libelle: 'Expéditeur (facultatif)' },
            ]
          : [];
  const avecConfiguration = champs.length > 0;
  const libelleSecret = c.type === 'webhook_sortant' ? 'Secret de signature HMAC' : c.type === 'courriel' ? 'Mot de passe SMTP' : c.type === 'sms' ? 'Jeton du fournisseur' : null;
  const envoi = useMutation({
    mutationFn: async () => {
      const configuration: Record<string, unknown> = {};
      for (const ch of champs) {
        const v = valeurs[ch.cle]?.trim() ?? '';
        if (v === '') continue;
        configuration[ch.cle] = ch.type === 'number' ? Number(v) : v;
      }
      if (c.type === 'courriel') configuration.securise = valeurs.securise === 'true';
      return api(`/api/v1/integrations/${c.id}`, {
        methode: 'PATCH',
        corps: { motif, ...(avecConfiguration ? { configuration } : {}), ...(secret ? { secret } : {}), ...(c.type === 'webhook_sortant' ? { evenements } : {}) },
      });
    },
    onSuccess: async () => {
      setSecret('');
      setMotif('');
      await surEnregistrement('Configuration enregistrée ; un connecteur actif repasse en test.');
    },
  });
  if (!avecConfiguration && !libelleSecret)
    return (
      <section className="carte-bloc" aria-labelledby="titre-configuration">
        <h2 id="titre-configuration">Configuration</h2>
        <p className="attenue">Ce connecteur n’a pas de configuration propre : il s’appuie sur les clés d’API attribuées aux organisations.</p>
      </section>
    );
  return (
    <form
      className="carte-bloc"
      aria-labelledby="titre-configuration"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <h2 id="titre-configuration">Configuration</h2>
      {champs.map((ch) => (
        <div key={ch.cle} className="champ">
          <label htmlFor={`conf-${ch.cle}`}>{ch.libelle}</label>
          <input id={`conf-${ch.cle}`} type={ch.type ?? 'text'} value={valeurs[ch.cle] ?? ''} onChange={(e) => setValeurs((v) => ({ ...v, [ch.cle]: e.target.value }))} />
        </div>
      ))}
      {c.type === 'courriel' && (
        <label className="case">
          <input type="checkbox" checked={valeurs.securise === 'true'} onChange={(e) => setValeurs((v) => ({ ...v, securise: String(e.target.checked) }))} />
          <span>Connexion chiffrée (TLS)</span>
        </label>
      )}
      {libelleSecret && (
        <div className="champ">
          <label htmlFor="conf-secret">{libelleSecret}</label>
          <input id="conf-secret" type="password" autoComplete="new-password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder={c.secretDefini ? 'Défini, jamais relu : saisir pour remplacer' : 'Non défini'} />
          <span className="aide">16 caractères au moins ; conservé chiffré, jamais affiché.</span>
        </div>
      )}
      {c.type === 'webhook_sortant' && (
        <fieldset className="champ">
          <legend>Événements souscrits</legend>
          {(referentiel?.evenements ?? []).map((ev) => (
            <label key={ev.code} className="case">
              <input type="checkbox" checked={evenements.includes(ev.code)} onChange={(e) => setEvenements((l) => (e.target.checked ? [...l, ev.code] : l.filter((x) => x !== ev.code)))} />
              <span>{ev.libelle}</span>
            </label>
          ))}
        </fieldset>
      )}
      <div className="champ">
        <label htmlFor="conf-motif">Motif</label>
        <input id="conf-motif" value={motif} onChange={(e) => setMotif(e.target.value)} />
      </div>
      {envoi.error && <MessageErreur erreur={envoi.error} titre="Configuration refusée" />}
      <button type="submit" className="bouton primaire" disabled={motif.trim().length < 3 || envoi.isPending || (secret !== '' && secret.length < 16)}>
        Enregistrer la configuration
      </button>
    </form>
  );
}
